import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiTrash2, FiEdit, FiUser, FiDollarSign, FiShoppingCart, FiX, FiMail, FiTrendingUp, FiSearch, FiAward, FiClock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import Spinner from '../components/Spinner';
import ConfirmModal from '../components/ConfirmModal';

const AddUserModal = ({ isOpen, onClose, onSave }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('vendeur');

  if (!isOpen) return null;

  const handleSave = () => {
    if (email) {
      onSave({ email, role });
      setEmail('');
      setRole('vendeur');
    } else {
      toast.error("L'email de l'employé est obligatoire.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Inviter un nouvel employé</h3>
        <p className="text-sm text-gray-600 mb-4">L'employé recevra une invitation par email pour créer son mot de passe et rejoindre votre boutique.</p>
        <input type="email" placeholder="Email de l'employé" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-2 border rounded mb-4" />
        <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-2 border rounded mb-4 bg-white">
          <option value="vendeur">Vendeur</option>
          <option value="manager">Manager</option>
        </select>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Envoyer l'invitation</button>
        </div>
      </div>
    </div>
  );
};

const EditUserModal = ({ isOpen, onClose, onSave, user }) => {
  const [role, setRole] = useState('');

  useEffect(() => {
    if (user) {
      setRole(user.role);
    }
  }, [user]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(user._id, { role });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Modifier le rôle de {user?.username}</h3>
        <p className="text-sm text-gray-600 mb-4">Choisissez le nouveau rôle pour cet employé.</p>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-2 border rounded mb-4 bg-white">
          <option value="vendeur">Vendeur</option>
          <option value="manager">Manager</option>
        </select>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-blue hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const ViewUserModal = ({ isOpen, onClose, userId }) => {
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      const fetchMemberDetail = async () => {
        setLoading(true);
        try {
          const response = await api.get(`/team/${userId}`);
          setMemberData(response.data);
        } catch (error) {
          toast.error("Impossible de charger les détails de l'employé.");
          setMemberData(null);
        } finally {
          setLoading(false);
        }
      };
      fetchMemberDetail();
    } else {
        setMemberData(null);
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  // Extraction sécurisée des données : gère le cas où memberData est l'utilisateur lui-même ou un objet conteneur
  const user = memberData?.user || memberData;
  const salesCount = user?.salesCount ?? memberData?.stats?.totalSalesCount ?? 0;
  const totalSalesValue = user?.totalSalesValue ?? memberData?.stats?.totalSalesValue ?? 0;
  const recentSales = memberData?.recentSales || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
         <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-semibold">Détails de l'employé</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FiX size={24} /></button>
        </div>
        
        <div className="p-6 overflow-y-auto">
            {loading ? (
                <Spinner message="Chargement des détails..." />
            ) : user && user.username ? (
                <>
                    <div className="flex items-center mb-8">
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                            <FiUser className="w-8 h-8 text-gray-500" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-bold text-gray-800">{user.username}</h2>
                                <span className={`capitalize text-xs font-semibold px-2 py-1 rounded-full ${ user.role === 'owner' ? 'bg-green-200 text-green-900' : user.role === 'manager' ? 'bg-purple-200 text-purple-900' : 'bg-blue-200 text-blue-900' }`}>
                                    {user.role === 'owner' ? 'Propriétaire' : user.role}
                                </span>
                            </div>
                            <p className="text-gray-500 flex items-center mt-1"><FiMail className="mr-2" size={14}/> {user.email}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        <div className="bg-blue-50 p-4 rounded-xl flex items-center gap-4 border border-blue-100">
                            <div className="bg-blue-200 p-3 rounded-full"><FiShoppingCart className="h-6 w-6 text-blue-800" /></div>
                            <div>
                                <p className="text-sm text-blue-700">Ventes réalisées</p>
                                <p className="font-bold text-xl text-blue-900">{salesCount}</p>
                            </div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl flex items-center gap-4 border border-green-100">
                            <div className="bg-green-200 p-3 rounded-full"><FiTrendingUp className="h-6 w-6 text-green-800" /></div>
                            <div>
                                <p className="text-sm text-green-700">Chiffre d'affaires</p>
                                <p className="font-bold text-xl text-green-900">{totalSalesValue.toLocaleString('fr-FR')} CFA</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center"><FiDollarSign className="mr-2"/>Ventes Récentes</h4>
                        <div className="overflow-x-auto">
                             {recentSales.length > 0 ? (
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="py-3 px-4">Date</th><th className="py-3 px-4">Articles</th><th className="py-3 px-4 text-right">Total</th></tr></thead>
                                    <tbody>
                                        {recentSales.map(sale => (
                                            <tr key={sale._id} className="border-b hover:bg-gray-50">
                                                <td className="py-3 px-4 font-medium text-gray-900">{new Date(sale.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                                                <td className="py-3 px-4">{sale.items.length}</td>
                                                <td className="py-3 px-4 text-right font-semibold text-brand-blue">{sale.totalAmount.toLocaleString('fr-FR')} CFA</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">Aucune vente récente.</p>}
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-center text-red-500 py-10">Données non disponibles.</div>
            )}
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end rounded-b-lg">
            <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Fermer</button>
        </div>
      </div>
    </div>
  );
};

const Team = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/team');
      setUsers(response.data);
    } catch (error) {
      toast.error("Collaborez avec vos employés en toute sécurité. La gestion d'équipe est une fonctionnalité Premium.", { icon: "👥" });
      setUsers([]); // Assurez-vous que users est un tableau même en cas d'erreur
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInviteUser = async (userData) => {
    try {
      await api.post('/team/invite', userData);
      toast.success(`Invitation envoyée à ${userData.email} !`);
      fetchUsers(); // Rafraîchir la liste après l'invitation
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de l'invitation.");
    }
    setIsModalOpen(false);
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleOpenViewModal = (userId) => {
    setViewingUserId(userId);
    setIsViewModalOpen(true);
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await api.put(`/team/${userId}`, userData);
      toast.success("Rôle de l'utilisateur mis à jour !");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de la mise à jour.");
    }
    setIsEditModalOpen(false);
  };

  const handleDeleteRequest = (user) => {
    setUserToDelete(user);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/team/${userToDelete._id}`);
      toast.success("Utilisateur supprimé avec succès.");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la suppression.");
    } finally {
      setIsConfirmModalOpen(false);
      setUserToDelete(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Identifier le meilleur vendeur
  const topSellerId = useMemo(() => {
    if (!users || users.length === 0) return null;
    const sellers = users.filter(u => u.salesCount > 0);
    if (sellers.length === 0) return null;
    return sellers.reduce((prev, current) => (prev.salesCount > current.salesCount) ? prev : current)._id;
  }, [users]);

  if (loading) {
    return <Spinner message="Chargement de l'équipe..." />;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Gestion d'équipe</h2>
          <p className="text-gray-500 text-sm mt-1">{users.length} membre{users.length > 1 ? 's' : ''} dans l'équipe</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg flex items-center shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5">
          <FiPlus className="mr-2" /> Inviter un employé
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <FiSearch className="text-gray-400 h-5 w-5" />
        </div>
        <input 
            type="text" 
            placeholder="Rechercher un membre par nom ou email..." 
            className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers && filteredUsers.length > 0 ? filteredUsers.map(user => (
          <div key={user._id} className={`bg-white p-6 rounded-2xl shadow-sm flex flex-col justify-between border border-gray-100 border-t-4 ${user.role === 'owner' ? 'border-t-green-500' : user.role === 'manager' ? 'border-t-purple-500' : 'border-t-blue-500'} hover:shadow-md transition-all duration-200`}>
            <div>
              <div className="flex justify-between items-start">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4"><FiUser className="w-6 h-6 text-gray-500"/></div>
                  <div>
                    <p className="text-gray-900 font-bold">{user.username}</p>
                    <p className="text-gray-600 text-sm">{user.email}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <span className={`capitalize text-xs font-semibold px-2 py-1 rounded-full ${ user.role === 'owner' ? 'bg-green-200 text-green-900' : user.role === 'manager' ? 'bg-purple-200 text-purple-900' : 'bg-blue-200 text-blue-900' }`}>
                    {user.role === 'owner' ? 'Propriétaire' : user.role}
                    </span>
                    {user.status === 'pending' && (
                        <span className="bg-orange-100 text-orange-800 text-xs font-bold px-2 py-1 rounded-full flex items-center border border-orange-200" title="Invitation envoyée, en attente de validation"><FiClock className="mr-1"/> En attente</span>
                    )}
                    {user._id === topSellerId && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full flex items-center border border-yellow-200 shadow-sm"><FiAward className="mr-1"/> Top Vendeur</span>
                    )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div className="flex items-center"><FiShoppingCart className="mr-2 text-gray-500"/> Ventes: <span className="font-bold ml-1">{user.salesCount || 0}</span></div>
                <div className="flex items-center"><FiDollarSign className="mr-2 text-gray-500"/> CA: <span className="font-bold ml-1">{(user.totalSalesValue || 0).toLocaleString('fr-FR')}</span></div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 border-t pt-4 mt-4">
              {user.role !== 'owner' && <button onClick={() => handleOpenEditModal(user)} className="text-gray-500 hover:text-brand-blue" title="Modifier le rôle"><FiEdit /></button>}
              {user.role !== 'owner' && <button onClick={() => handleDeleteRequest(user)} className="text-red-500 hover:text-red-700" title="Supprimer l'utilisateur"><FiTrash2 /></button>}
              <button onClick={() => handleOpenViewModal(user._id)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-bold py-1 px-3 rounded-lg">Voir détails</button>
            </div>
          </div>
        )) : (
          <p className="col-span-full text-center py-10 text-gray-500">Aucun membre trouvé.</p>
        )}
      </div>

      <AddUserModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleInviteUser} />
      <EditUserModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleUpdateUser} user={editingUser} />
      <ViewUserModal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} userId={viewingUserId} />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer l'employé"
        message={`Êtes-vous sûr de vouloir supprimer "${userToDelete?.username}" de votre équipe ? Cette action est irréversible.`}
      />
    </div>
  );
};

export default Team;